import { useState } from 'react';
import { Plus, Trash2, UserCog } from 'lucide-react';
import {
  useAssignments,
  useCreateAssignment,
  useDeleteAssignment,
} from '@/hooks/useAssignments';
import { useUsers } from '@/hooks/useUsers';
import { useServers } from '@/hooks/useServers';
import { Modal } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';

export function AssignmentsPage() {
  const list = useAssignments();
  const del = useDeleteAssignment();
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-moss-200">Asignaciones</h1>
          <p className="mt-1 text-sm text-moss-300/70">
            Define qué colegios puede ver cada cobrador. Sin db_name = todos los colegios del servidor.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> Nueva asignación
        </button>
      </div>

      {list.data?.rows.length === 0 ? (
        <EmptyState
          icon={UserCog}
          title="Sin asignaciones aún"
          description="Asigna servidores o colegios específicos a tus cobradores."
          action={
            <button className="btn-primary" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> Nueva asignación
            </button>
          }
        />
      ) : (
        <div className="glass-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-700/70 bg-ink-800/40 text-left text-xs uppercase tracking-wide text-moss-300/70">
              <tr>
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Servidor</th>
                <th className="px-4 py-3">db_name</th>
                <th className="px-4 py-3">Creada</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.rows.map((a) => (
                <tr key={a.id} className="border-b border-ink-700/40 last:border-0 hover:bg-ink-700/30">
                  <td className="px-4 py-3 text-moss-200">{a.user_email}</td>
                  <td className="px-4 py-3 text-moss-300">{a.server_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-mustard-300">
                    {a.db_name ?? '(todos)'}
                  </td>
                  <td className="px-4 py-3 text-xs text-moss-300">
                    {new Date(a.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button
                        className="btn-ghost h-8 px-2 text-xs hover:border-danger/60 hover:text-danger"
                        onClick={() => {
                          if (confirm('¿Eliminar esta asignación?')) del.mutate(a.id);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateAssignmentModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function CreateAssignmentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateAssignment();
  const users = useUsers();
  const servers = useServers();
  const [userId, setUserId] = useState('');
  const [serverId, setServerId] = useState('');
  const [dbName, setDbName] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({
        user_id: userId,
        server_id: serverId,
        db_name: dbName.trim() || null,
      });
      setUserId('');
      setServerId('');
      setDbName('');
      onClose();
    } catch {
      /* shown below */
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nueva asignación">
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Usuario">
          <select required value={userId} onChange={(e) => setUserId(e.target.value)} className="input-base">
            <option value="">— Selecciona —</option>
            {users.data?.rows.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email} ({u.role})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Servidor">
          <select required value={serverId} onChange={(e) => setServerId(e.target.value)} className="input-base">
            <option value="">— Selecciona —</option>
            {servers.data?.rows.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="db_name (opcional — vacío = todos los colegios del servidor)">
          <input
            className="input-base font-mono"
            value={dbName}
            onChange={(e) => setDbName(e.target.value)}
            placeholder="colegio_xyz_2026"
          />
        </Field>
        {create.isError && <div className="text-sm text-danger">{(create.error as Error).message}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wide text-moss-300">{label}</label>
      {children}
    </div>
  );
}
